"use client"
import { motion } from 'motion/react'
import Tooltip from '@/ui/Tooltip'

// One ten-billionth of space per person, ~10.2B people in 2040 → total parcels.
const TOTAL_PARCELS = 10_200_000_000

// The schools of thought people sort their galaxy of resources into (see the
// epilogue prose). Each is one segment of the single proportional row. Every
// segment draws as a black outline; they are distinguished only by their
// tooltips.
type ParcelCategory = {
  key: string
  name: string
  description: string
}

const CATEGORIES: ParcelCategory[] = [
  {
    key: 'individuals',
    name: '21st Century Individuals',
    description: 'Every human in 2040 is randomly assigned one ten-billionth of space. Parcels farther from Earth are larger. They can be traded or donated.',
  },
  {
    key: 'defer',
    name: 'Defer to the Future',
    description: 'Give most resources to your children (or other future people) and let them choose what to do with them.',
  },
  {
    key: 'flourishing',
    name: 'Human Flourishing',
    description: 'Set up worlds of normal, free people living ordinary lives — some post-scarcity, some rolling back technology to leave room for human labor.',
  },
  {
    key: 'simulated',
    name: 'Simulated Human Flourishing',
    description: 'Run vast numbers of simulated happy civilizations: a planet-size computer can simulate the equivalent of a million planets of flourishing lives.',
  },
  {
    key: 'acausal',
    name: 'Acausal Trade',
    description: 'Pursue a compromise between our values and those of unreachable beings in distant galaxies, on the reasoning that they may do the same for us.',
  },
  {
    key: 'other',
    name: 'Other',
    description: 'Everything else — hedonium, nature preserves, historical re-enactment, and the countless other plans too many to number.',
  },
]

// Made-up ownership shares over the epilogue timeline. Each row is the
// fraction of all parcels in a category, in CATEGORIES order; rows sum to 1.
// Everything starts in the hands of individuals (the undivided outline bar)
// and gradually drains into the trusts as the schools of thought consolidate.
const YEARLY_SHARES: Record<number, number[]> = {
  //      individ  defer  flourish  simulated  acausal  other
  2041:  [1.00,   0.00,  0.00,     0.00,      0.00,    0.00],
  2045:  [0.55,   0.13,  0.12,     0.07,      0.05,    0.08],
  2050:  [0.30,   0.17,  0.19,     0.15,      0.09,    0.10],
  2055:  [0.12,   0.19,  0.26,     0.23,      0.12,    0.08],
  2060:  [0.02,   0.18,  0.30,     0.29,      0.14,    0.07],
}

const SHARE_YEARS = Object.keys(YEARLY_SHARES).map(Number).sort((a, b) => a - b)

// Linearly interpolate the shares for an arbitrary year between the keyframes.
const getSharesForYear = (year: number): number[] => {
  if (year <= SHARE_YEARS[0]) return YEARLY_SHARES[SHARE_YEARS[0]]
  if (year >= SHARE_YEARS[SHARE_YEARS.length - 1]) return YEARLY_SHARES[SHARE_YEARS[SHARE_YEARS.length - 1]]
  for (let i = 0; i < SHARE_YEARS.length - 1; i++) {
    const lo = SHARE_YEARS[i]
    const hi = SHARE_YEARS[i + 1]
    if (year >= lo && year <= hi) {
      const t = (year - lo) / (hi - lo)
      const loShares = YEARLY_SHARES[lo]
      const hiShares = YEARLY_SHARES[hi]
      return loShares.map((s, j) => s + (hiShares[j] - s) * t)
    }
  }
  return YEARLY_SHARES[SHARE_YEARS[SHARE_YEARS.length - 1]]
}

// Fractional year (e.g. 2043.5) so segment widths track the animated date
// continuously instead of stepping at year boundaries.
const getFractionalYear = (date: Date): number => {
  const year = date.getFullYear()
  const yearStart = Date.UTC(year, 0, 1)
  const yearEnd = Date.UTC(year + 1, 0, 1)
  return year + (date.getTime() - yearStart) / (yearEnd - yearStart)
}

// Hide segments whose share has interpolated to (effectively) zero — a
// zero-width flex child would still contribute its 2px gap and could steal
// the row's corner rounding.
const MIN_VISIBLE_SHARE = 0.001

const formatParcels = (parcels: number): string => {
  if (parcels >= 1_000_000_000) return `${(parcels / 1_000_000_000).toFixed(1)}B`
  if (parcels >= 1_000_000) return `${Math.round(parcels / 1_000_000)}M`
  return parcels.toLocaleString()
}

type ParcelSegmentProps = {
  category: ParcelCategory
  share: number
  isFirst: boolean
  isLast: boolean
}

const ParcelSegment = ({ category, share, isFirst, isLast }: ParcelSegmentProps) => {
  const parcels = Math.round(share * TOTAL_PARCELS)
  return (
    <Tooltip
      placement="top"
      maxWidth={250}
      content={<div className="p-2 font-mono text-[11px]">
        <div className="font-semibold uppercase">{category.name}</div>
        <div className="opacity-80 mt-1 font-sans">{category.description}</div>
        <div className="opacity-80 mt-1">~{formatParcels(parcels)} parcels · {Math.round(share * 100)}%</div>
      </div>}
    >
      <motion.div
        className={`h-full basis-0 min-w-0 overflow-hidden cursor-default border border-black ${isFirst ? 'rounded-l-[3px]' : ''} ${isLast ? 'rounded-r-[3px]' : ''}`}
        initial={false}
        animate={{ flexGrow: share }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </Tooltip>
  )
}

export const SpaceParcelOwnership = ({ currentDate }: { currentDate: Date }) => {
  const year = getFractionalYear(currentDate)
  if (year < 2040) return null
  const shares = getSharesForYear(year)
  const segments = CATEGORIES
    .map((category, i) => ({ category, share: shares[i] }))
    .filter(({ share }) => share > MIN_VISIBLE_SHARE)
  return (
    <div className="flex w-full h-[22px] gap-[2px]">
      {segments.map(({ category, share }, i) => (
        <ParcelSegment
          key={category.key}
          category={category}
          share={share}
          isFirst={i === 0}
          isLast={i === segments.length - 1}
        />
      ))}
    </div>
  )
}
